// src/pages/landing-page/LoglVideoSection.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { trackYouTubeLanguageClick } from '../../utils/analytics';
import Icon from '../../components/AppIcon';

const LoglVideoSection = () => {
  const { t, i18n } = useTranslation();
  const content = {
    title: t('loglVideo.title'),
    description: t('loglVideo.description'),
    watch: t('loglVideo.watch'),
  };

  // 언어별 YouTube URL (실제 영상 ID로 교체 가능)
  const youtubeUrls = {
    local: {
      ko: 'https://youtu.be/6NEbgC0eIiI',
      en: 'https://youtu.be/pyyGiQo91QI',
      ja: 'https://youtu.be/eXHmUdIBEBM',
    },
    global: {
      ko: 'https://www.youtube.com/embed/MwWASdzkI1s',
      en: 'https://www.youtube.com/embed/x123jnLwNks',
      ja: 'https://www.youtube.com/embed/DaQyz4UDCGU',
    }
  };

  // YouTube URL에서 비디오 ID 추출
  const getVideoId = (url) => {
    if (!url) return null;
    if (url.includes('/embed/')) return url.split('/embed/')[1]?.split('?')[0];
    if (url.includes('youtu.be/')) return url.split('youtu.be/')[1]?.split('?')[0];
    if (url.includes('youtube.com/watch?v=')) return url.split('v=')[1]?.split('&')[0];
    return null;
  };

  // 임베드 URL로 변환
  const convertToEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('/embed/')) return url;
    const videoId = getVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  // 썸네일 URL
  const getThumbnailUrl = (url) => {
    const videoId = getVideoId(url);
    if (!videoId) return null;
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  // 상태
  const [localVideoUrl, setLocalVideoUrl] = React.useState(null);
  const [globalVideoUrl, setGlobalVideoUrl] = React.useState(null);
  const [currentLocalLang, setCurrentLocalLang] = React.useState('en');
  const [currentGlobalLang, setCurrentGlobalLang] = React.useState('en');
  const [localVideoId, setLocalVideoId] = React.useState(null);
  const [globalVideoId, setGlobalVideoId] = React.useState(null);

  // 기본값 고정: Local=ko, Global=en (스크롤/리렌더에도 유지)
  React.useEffect(() => {
    const defaultLocal = 'ko';
    const defaultGlobal = 'en';

    if (!localVideoUrl) {
      const srcLocal = youtubeUrls?.local?.[defaultLocal];
      if (srcLocal) {
        const embedLocal = convertToEmbedUrl(srcLocal);
        const vidLocal = getVideoId(srcLocal);
        if (embedLocal) {
          setLocalVideoUrl(`${embedLocal}?rel=0&modestbranding=1`);
          setCurrentLocalLang(defaultLocal);
          if (vidLocal) setLocalVideoId(vidLocal);
        }
      }
    }
    if (!globalVideoUrl) {
      const srcGlobal = youtubeUrls?.global?.[defaultGlobal];
      if (srcGlobal) {
        const embedGlobal = convertToEmbedUrl(srcGlobal);
        const vidGlobal = getVideoId(srcGlobal);
        if (embedGlobal) {
          setGlobalVideoUrl(`${embedGlobal}?rel=0&modestbranding=1`);
          setCurrentGlobalLang(defaultGlobal);
          if (vidGlobal) setGlobalVideoId(vidGlobal);
        }
      }
    }
  }, [localVideoUrl, globalVideoUrl]);

  const handleLanguageClick = (lang) => {
    trackYouTubeLanguageClick(lang);
  };

  // 재생 핸들러: 한쪽 재생 시 반대쪽 autoplay 제거
  const handlePlay = (type, lang) => {
    const src = youtubeUrls?.[type]?.[lang];
    if (!src) return;
    const embedUrl = convertToEmbedUrl(src);
    if (!embedUrl) return;
    const videoId = getVideoId(src);
    if (!videoId) return;

    if (type === 'local') {
      if (localVideoId === videoId) { handleLanguageClick(lang); return; }
      setLocalVideoUrl(`${embedUrl}?autoplay=1&rel=0&modestbranding=1`);
      setLocalVideoId(videoId);
      setCurrentLocalLang(lang);
      if (globalVideoUrl) {
        const base = globalVideoUrl.split('?')[0];
        setGlobalVideoUrl(`${base}?rel=0&modestbranding=1`);
      }
    } else if (type === 'global') {
      if (globalVideoId === videoId) { handleLanguageClick(lang); return; }
      setGlobalVideoUrl(`${embedUrl}?autoplay=1&rel=0&modestbranding=1`);
      setGlobalVideoId(videoId);
      setCurrentGlobalLang(lang);
      if (localVideoUrl) {
        const base = localVideoUrl.split('?')[0];
        setLocalVideoUrl(`${base}?rel=0&modestbranding=1`);
      }
    }
    handleLanguageClick(lang);
  };

  const YouTubeIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-label="YouTube">
      <rect x="1" y="5" width="22" height="14" rx="4" fill="#FF0033" />
      <path d="M10 9L15 12L10 15V9Z" fill="white" />
    </svg>
  );

  const LangButtons = React.memo(function LangButtons({ type }) {
    return (
      <div className="mt-5 flex items-center justify-center gap-3">
        {[
          { code: 'ko', label: '한국어', emoji: '🇰🇷' },
          { code: 'en', label: 'English', emoji: '🇺🇸' },
          { code: 'ja', label: '日本語', emoji: '🇯🇵' }
        ].map(l => (
          <button
            key={l.code}
            onClick={() => handlePlay(type, l.code)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-gray-300/80 text-gray-800 hover:text-white hover:bg-gray-900 transition-colors shadow-sm bg-white"
            title={`${l.label} • Play`}
          >
            <span className="text-base leading-none">{l.emoji}</span>
            <span className="leading-none">{l.label}</span>
          </button>
        ))}
      </div>
    );
  });

  const VideoCard = React.memo(function VideoCard({ label, icon, videoUrl, fallbackUrl, videoId }) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="group"
      >
        <div className="relative rounded-2xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-shadow p-4">
          <div className="mb-3 flex items-center justify-center gap-2">
            <Icon name={icon} size={18} className="text-gray-800" />
            <span className="text-base font-semibold text-gray-900">{label}</span>
          </div>

          {/* iframe 컨테이너는 정적 div (모션 X) */}
          <div className="relative aspect-[16/9] rounded-[18px] p-[1.5px] overflow-hidden bg-[linear-gradient(135deg,rgba(255,255,255,0.7)_0%,rgba(255,255,255,0.25)_45%,rgba(255,255,255,0.7)_100%)]">
            <div className="relative w-full h-full rounded-[16px] bg-black overflow-hidden shadow-[0_20px_48px_rgba(0,0,0,0.35)] ring-1 ring-white/10" style={{ willChange: 'transform', transform: 'translateZ(0)' }}>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/12 via-transparent to-transparent" />
              <div className="pointer-events-none absolute inset-0 rounded-[16px] shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]" />

              {videoUrl ? (
                <iframe
                  key={videoId || label}
                  title={`${label} YouTube`}
                  src={videoUrl}
                  className="w-full h-full object-cover"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  loading="eager"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <img
                  loading="lazy"
                  src={fallbackUrl || "/fallback-video.png"}
                  alt={`${label} Video Capture`}
                  className="w-full h-full object-cover"
                />
              )}

              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -inset-y-10 -left-1/3 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 opacity-0 group-hover:opacity-100 group-hover:translate-x-[220%]" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  });

  return (
    <div className="w-full mx-auto px-0">
      {/* Title */}
      <div className="text-center mb-12 px-0 sm:px-4 pt-0 pt-4 sm:pt-12">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          {content.title}
        </h2>
        {/* description 제거 요청에 따라 비표시 */}
      </div>

      {/* 단일 레이아웃: 모바일 1열 / 데스크톱 2열 (중앙 정렬 강화) */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 px-4 items-start">
        {/* Local */}
        <div>
          <VideoCard
            label="Local"
            icon="Home"
            videoUrl={localVideoUrl}
            videoId={localVideoId}
            fallbackUrl={getThumbnailUrl(youtubeUrls?.local?.[currentLocalLang]) || "/local video.png"}
          />
          <LangButtons type="local" />
        </div>

        {/* Global */}
        <div>
          <VideoCard
            label="Global"
            icon="Plane"
            videoUrl={globalVideoUrl}
            videoId={globalVideoId}
            fallbackUrl={getThumbnailUrl(youtubeUrls?.global?.[currentGlobalLang]) || "/global video.png"}
          />
          <LangButtons type="global" />
        </div>
      </div>
    </div>
  );
};

export default React.memo(LoglVideoSection);
